export const SETTLEMENT_WORKSPACE_TABS = [
  { key: "summary", label: "Summary" },
  { key: "eligibility", label: "Eligibility" },
  { key: "fee-calculation", label: "Fee Calculation" },
  { key: "adjustments", label: "Adjustments" },
  { key: "statement", label: "Settlement Statement" },
  { key: "billing-handoff", label: "Billing Handoff" },
  { key: "history", label: "Audit & Versions" },
] as const;

export type SettlementWorkspaceTab = (typeof SETTLEMENT_WORKSPACE_TABS)[number]["key"];

export const SETTLEMENT_FINANCIAL_BOUNDARY =
  "Settlement Center determines CollabRecover recovery fees from governed recovery activity. It does not post cash application or mutate client accounting balances.";

export const SETTLEMENT_BILLING_HANDOFF_BOUNDARY =
  "Settlement Center creates the approved billing handoff. Billing Center owns invoice approval, delivery, payment collection, receipts, reminders, aging, and delinquency.";

export const FEE_RULE_REQUIRED_MESSAGE = "Fee rule required";

export const SETTLEMENT_REASON_ACTIONS = [
  "create_draft",
  "confirm_eligibility",
  "mark_ineligible",
  "pending_review",
  "return_source_payment",
  "exclude_line",
  "adjust_qualifying_amount",
  "fee_override",
  "add_explanation",
  "settlement_adjustment",
  "submit_review",
  "return_correction",
  "reject",
  "void",
  "reverse",
  "create_replacement",
  "fee_rule_create",
  "fee_rule_update",
  "generate_statement",
] as const;

export type SettlementReasonAction = (typeof SETTLEMENT_REASON_ACTIONS)[number];

export type SettlementDraftCandidate = {
  eligibilityId: string;
  clientId: string;
  currency: string;
  classification: string;
  qualifyingAmountCents: number;
  nonQualifyingAmountCents?: number;
  estimatedFeeCents?: number | null;
};

export type SettlementDraftSelection = {
  clientId: string;
  settlementPeriodStart: string;
  settlementPeriodEnd: string;
  eligibilityIds: string[];
  reason: string;
};

export type SettlementFeeRuleModule = "PERCENTAGE" | "FLAT" | "AGE_TIER";

export type SettlementFeeRuleAgeTier = {
  fromDays: string;
  toDays: string;
  feeRatePercent: string;
  minimumFee: string;
  maximumFee: string;
};

export type SettlementFeeRuleFormState = {
  clientId: string;
  placementId: string;
  ruleName: string;
  ruleType: SettlementFeeRuleModule;
  feeRatePercent: string;
  flatFee: string;
  minimumFee: string;
  maximumFee: string;
  ageTiers: SettlementFeeRuleAgeTier[];
  effectiveDate: string;
  expirationDate: string;
  currency: string;
  status: "ACTIVE" | "INACTIVE";
  changeReason: string;
};

const EMPTY_AGE_TIER: SettlementFeeRuleAgeTier = {
  fromDays: "0",
  toDays: "",
  feeRatePercent: "",
  minimumFee: "",
  maximumFee: "",
};

export function createEmptyFeeRuleForm(): SettlementFeeRuleFormState {
  return {
    clientId: "",
    placementId: "",
    ruleName: "",
    ruleType: "PERCENTAGE",
    feeRatePercent: "",
    flatFee: "",
    minimumFee: "",
    maximumFee: "",
    ageTiers: [{ ...EMPTY_AGE_TIER }],
    effectiveDate: "",
    expirationDate: "",
    currency: "USD",
    status: "ACTIVE",
    changeReason: "",
  };
}

export function isSettlementWorkspaceTab(value: string): value is SettlementWorkspaceTab {
  return SETTLEMENT_WORKSPACE_TABS.some((tab) => tab.key === value);
}

export function normalizeSettlementWorkspaceTab(value: string | null | undefined): SettlementWorkspaceTab {
  if (value === "lines" || value === "manual") return "fee-calculation";
  if (value === "invoice") return "billing-handoff";
  if (value === "delinquency" || value === "reporting") return "summary";
  return isSettlementWorkspaceTab(value || "") ? value as SettlementWorkspaceTab : "summary";
}

export function settlementWorkspaceTabLabels() {
  return SETTLEMENT_WORKSPACE_TABS.map((tab) => tab.label);
}

export function dollarsToCents(value: string) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

export function percentToBasisPoints(value: string) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

export function basisPointsToPercent(value: number | null | undefined) {
  if (value == null) return "";
  return (Number(value) / 100).toFixed(Number(value) % 100 === 0 ? 0 : 2);
}

export function centsToDollarsInput(value: number | null | undefined) {
  if (value == null) return "";
  return (Number(value) / 100).toFixed(2);
}

export function validateSettlementDraftSelection(
  input: SettlementDraftSelection,
  rows: SettlementDraftCandidate[],
) {
  const errors: string[] = [];
  const clientId = String(input.clientId || "").trim();
  const periodStart = String(input.settlementPeriodStart || "").trim();
  const periodEnd = String(input.settlementPeriodEnd || "").trim();
  const reason = String(input.reason || "").trim();
  const eligibilityIds = [...new Set((input.eligibilityIds || []).map((id) => String(id || "").trim()).filter(Boolean))];
  if (!clientId) errors.push("Client is required.");
  if (!periodStart) errors.push("Settlement period start is required.");
  if (!periodEnd) errors.push("Settlement period end is required.");
  if (periodStart && periodEnd && Date.parse(periodStart) > Date.parse(periodEnd)) {
    errors.push("Settlement period start must be before the period end.");
  }
  if (!eligibilityIds.length) errors.push("Select at least one eligible recovery record.");
  if (!reason) errors.push("A draft creation reason is required.");
  const selected = rows.filter((row) => eligibilityIds.includes(row.eligibilityId));
  if (selected.length !== eligibilityIds.length) errors.push("Every selected record must remain available for the chosen client.");
  if (selected.some((row) => row.clientId !== clientId)) errors.push("Selected recovery records must belong to the chosen client.");
  if (selected.some((row) => row.classification !== "ELIGIBLE")) errors.push("Only eligible recovery records may be selected.");
  const currencies = [...new Set(selected.map((row) => String(row.currency || "").toUpperCase()))];
  if (currencies.length > 1) errors.push("Selected recovery records must share one currency.");
  return {
    ok: errors.length === 0,
    errors,
    clientId,
    periodStart,
    periodEnd,
    reason,
    eligibilityIds,
    selected,
    currency: currencies[0] || "",
  };
}

export function previewSettlementDraft(rows: SettlementDraftCandidate[]) {
  const selected = rows.filter((row) => row.classification === "ELIGIBLE");
  const grossQualifyingRecoveryCents = selected.reduce((sum, row) => sum + Number(row.qualifyingAmountCents || 0), 0);
  const excludedNonQualifyingRecoveryCents = selected.reduce((sum, row) => sum + Number(row.nonQualifyingAmountCents || 0), 0);
  const estimatedRecoveryFeeCents = selected.reduce((sum, row) => sum + Number(row.estimatedFeeCents || 0), 0);
  return {
    selectedRecoveryCount: selected.length,
    grossQualifyingRecoveryCents,
    excludedNonQualifyingRecoveryCents,
    estimatedRecoveryFeeCents,
    adjustmentCents: 0,
    totalReadyForSettlementCents: estimatedRecoveryFeeCents,
  };
}

export function selectableDraftRows(rows: SettlementDraftCandidate[], clientId: string, currency?: string) {
  const selectedClient = String(clientId || "").trim();
  const selectedCurrency = String(currency || "").trim().toUpperCase();
  return rows.filter((row) => {
    if (row.classification !== "ELIGIBLE") return false;
    if (selectedClient && row.clientId !== selectedClient) return false;
    if (selectedCurrency && String(row.currency || "").toUpperCase() !== selectedCurrency) return false;
    return true;
  });
}

export function draftRowSelectionLockedCurrency(rows: SettlementDraftCandidate[], selectedIds: string[]) {
  const selected = rows.filter((row) => selectedIds.includes(row.eligibilityId));
  const currencies = [...new Set(selected.map((row) => String(row.currency || "").toUpperCase()).filter(Boolean))];
  return currencies.length === 1 ? currencies[0] : "";
}

export function canSelectDraftRow(
  row: SettlementDraftCandidate,
  clientId: string,
  lockedCurrency: string,
  selectedIds: string[],
) {
  if (row.classification !== "ELIGIBLE") return false;
  if (!clientId || row.clientId !== clientId) return false;
  if (lockedCurrency && String(row.currency || "").toUpperCase() !== lockedCurrency && !selectedIds.includes(row.eligibilityId)) {
    return false;
  }
  return true;
}

export function validateFeeRuleForm(form: SettlementFeeRuleFormState) {
  const errors: string[] = [];
  if (!String(form.clientId || "").trim()) errors.push("Client is required.");
  if (!String(form.ruleName || "").trim()) errors.push("Rule name is required.");
  if (!String(form.effectiveDate || "").trim()) errors.push("Effective date is required.");
  if (!String(form.changeReason || "").trim()) errors.push("A fee-rule change reason is required.");
  if (!String(form.currency || "").trim()) errors.push("Currency is required.");
  if (form.ruleType === "PERCENTAGE" && percentToBasisPoints(form.feeRatePercent) == null) {
    errors.push("Percentage rate is required.");
  }
  if (form.ruleType === "FLAT" && dollarsToCents(form.flatFee) == null) {
    errors.push("Flat fee amount is required.");
  }
  if (form.ruleType === "AGE_TIER") {
    const tiers = (form.ageTiers || []).filter((tier) => String(tier.fromDays || "").trim() !== "" || String(tier.feeRatePercent || "").trim() !== "");
    if (!tiers.length) errors.push("At least one age tier is required.");
    tiers.forEach((tier, index) => {
      if (String(tier.fromDays || "").trim() === "" || Number.isNaN(Number(tier.fromDays))) {
        errors.push(`Age tier ${index + 1} requires from days.`);
      }
      if (percentToBasisPoints(tier.feeRatePercent) == null) {
        errors.push(`Age tier ${index + 1} requires a percentage rate.`);
      }
    });
  }
  return { ok: errors.length === 0, errors };
}

export function feeRuleModuleFields(ruleType: SettlementFeeRuleModule) {
  return {
    percentageActive: ruleType === "PERCENTAGE",
    flatActive: ruleType === "FLAT",
    ageActive: ruleType === "AGE_TIER",
    minMaxOptional: true,
  };
}

export function feeRulePayloadsFromForm(form: SettlementFeeRuleFormState) {
  const validation = validateFeeRuleForm(form);
  if (!validation.ok) return { ok: false as const, errors: validation.errors, payloads: [] as Array<Record<string, unknown>> };
  const shared = {
    clientId: form.clientId.trim(),
    placementId: form.placementId.trim() || null,
    ruleName: form.ruleName.trim(),
    ruleType: form.ruleType,
    effectiveDate: form.effectiveDate,
    expirationDate: form.expirationDate.trim() || null,
    currency: form.currency.trim().toUpperCase(),
    status: form.status,
    changeReason: form.changeReason.trim(),
    minimumFeeCents: dollarsToCents(form.minimumFee),
    maximumFeeCents: dollarsToCents(form.maximumFee),
  };
  if (form.ruleType === "PERCENTAGE") {
    return {
      ok: true as const,
      errors: [],
      payloads: [{
        ...shared,
        feeRateBasisPoints: percentToBasisPoints(form.feeRatePercent),
        flatFeeCents: null,
        ageBucketFromDays: null,
        ageBucketToDays: null,
      }],
    };
  }
  if (form.ruleType === "FLAT") {
    return {
      ok: true as const,
      errors: [],
      payloads: [{
        ...shared,
        feeRateBasisPoints: null,
        flatFeeCents: dollarsToCents(form.flatFee),
        ageBucketFromDays: null,
        ageBucketToDays: null,
      }],
    };
  }
  const tiers = form.ageTiers.filter((tier) => String(tier.fromDays || "").trim() !== "" && percentToBasisPoints(tier.feeRatePercent) != null);
  return {
    ok: true as const,
    errors: [],
    payloads: tiers.map((tier, index) => ({
      ...shared,
      ruleName: tiers.length > 1 ? `${form.ruleName.trim()} · ${tier.fromDays}${tier.toDays ? `-${tier.toDays}` : "+"} days` : form.ruleName.trim(),
      feeRateBasisPoints: percentToBasisPoints(tier.feeRatePercent),
      flatFeeCents: null,
      ageBucketFromDays: Math.max(0, Math.trunc(Number(tier.fromDays))),
      ageBucketToDays: String(tier.toDays || "").trim() === "" ? null : Math.max(0, Math.trunc(Number(tier.toDays))),
      minimumFeeCents: dollarsToCents(tier.minimumFee) ?? shared.minimumFeeCents,
      maximumFeeCents: dollarsToCents(tier.maximumFee) ?? shared.maximumFeeCents,
      tierIndex: index,
    })),
  };
}

export function emptyAgeTier(): SettlementFeeRuleAgeTier {
  return { ...EMPTY_AGE_TIER };
}

export function settlementApprovalBlockers(input: {
  currentUserId?: string | null;
  preparedByUserId?: string | null;
  lastFinancialModifierUserId?: string | null;
  feeOverriderUserId?: string | null;
}) {
  const actor = String(input.currentUserId || "");
  if (!actor) return [];
  const blockers: Array<{ code: string; message: string }> = [];
  if (actor === String(input.preparedByUserId || "")) {
    blockers.push({
      code: "preparer",
      message: "Approval is unavailable because you prepared this Settlement. An independent reviewer must approve it.",
    });
  }
  if (actor === String(input.feeOverriderUserId || "")) {
    blockers.push({
      code: "fee_overrider",
      message: "Approval is unavailable because you applied a fee override on this Settlement. An independent reviewer must approve it.",
    });
  }
  if (actor === String(input.lastFinancialModifierUserId || "")) {
    blockers.push({
      code: "last_financial_modifier",
      message: "Approval is unavailable because you were the last financial modifier. An independent reviewer must approve it.",
    });
  }
  return blockers;
}

export function settlementActionAvailability(status: string, extras?: { replacementSettlementId?: string | null }) {
  const settlementStatus = String(status || "").toUpperCase();
  return {
    submitReview: settlementStatus === "DRAFT" || settlementStatus === "RETURNED",
    returnCorrection: settlementStatus === "AWAITING_APPROVAL",
    approve: settlementStatus === "AWAITING_APPROVAL",
    reject: settlementStatus === "AWAITING_APPROVAL",
    voidDraft: settlementStatus === "DRAFT" || settlementStatus === "RETURNED" || settlementStatus === "REJECTED",
    reverseApproved: settlementStatus === "APPROVED",
    createReplacement: settlementStatus === "REVERSED" && !extras?.replacementSettlementId,
    reviewSources: settlementStatus !== "REVERSED" && settlementStatus !== "VOIDED",
    adjustFinancials: settlementStatus === "DRAFT" || settlementStatus === "RETURNED" || settlementStatus === "AWAITING_APPROVAL",
    generateStatement: settlementStatus === "APPROVED",
  };
}

export function targetSettlementLine<T extends { lineId: string }>(lines: T[], lineId: string | null | undefined) {
  const requested = String(lineId || "").trim();
  if (!requested) return null;
  return lines.find((line) => line.lineId === requested) || null;
}

export function requiresOperatorReason(action: string) {
  return SETTLEMENT_REASON_ACTIONS.includes(action as SettlementReasonAction);
}

export function clientHasApplicableFeeRule(
  feeRules: Array<{ clientId: string; status?: string; currency?: string }>,
  clientId: string,
  currency?: string,
) {
  const selectedClient = String(clientId || "").trim();
  const selectedCurrency = String(currency || "").trim().toUpperCase();
  return feeRules.some((rule) => {
    if (rule.clientId !== selectedClient) return false;
    if (String(rule.status || "ACTIVE").toUpperCase() !== "ACTIVE") return false;
    if (selectedCurrency && String(rule.currency || "").toUpperCase() !== selectedCurrency) return false;
    return true;
  });
}

export function buildSettlementWorkbenchHref(input: {
  accountId?: string | null;
  clientId?: string | null;
  placementId?: string | null;
  returnRoute?: string | null;
}) {
  const params = new URLSearchParams();
  if (input.accountId) params.set("accountId", input.accountId);
  if (input.clientId) params.set("clientId", input.clientId);
  if (input.placementId) params.set("placementId", input.placementId);
  params.set("return", input.returnRoute || "account-recovery");
  return `/admin/ar-recovery/settlements?${params.toString()}`;
}

export function settlementReturnHref(returnRoute: string | null | undefined) {
  return returnRoute === "account-recovery" ? "/admin/ar-recovery/customer-accounts" : "";
}

export function previewSettlementAdjustment(currentAdjustmentCents: number, amountCents: number, direction: "increase" | "decrease") {
  const delta = direction === "decrease" ? -Math.abs(amountCents) : Math.abs(amountCents);
  return {
    adjustmentCents: delta,
    resultingAdjustmentCents: Number(currentAdjustmentCents || 0) + delta,
  };
}

export function lineFeeDisplay(line: {
  feeRuleType?: string | null;
  feeRateBasisPoints?: number | null;
  flatFeeCents?: number | null;
  ageBucketFromDays?: number | null;
  ageBucketToDays?: number | null;
}) {
  const type = String(line.feeRuleType || "PERCENTAGE").toUpperCase();
  if (type === "FLAT") return { type: "FLAT", value: line.flatFeeCents, label: "Flat amount" };
  if (type === "AGE_TIER" || type === "AGE_BASED") {
    const fromDays = line.ageBucketFromDays == null ? "0" : String(line.ageBucketFromDays);
    const toDays = line.ageBucketToDays == null ? "open-ended" : String(line.ageBucketToDays);
    return { type: "AGE_TIER", value: line.feeRateBasisPoints, label: `${fromDays}–${toDays} days` };
  }
  return { type: "PERCENTAGE", value: line.feeRateBasisPoints, label: "Percentage" };
}
